import React from "react";
import { Link } from "react-router-dom";
import { BrandLogo } from "@/components/tabdine/Shared";
import { Zap, Smartphone, Utensils, MessageSquare, Award, BarChart3, Gift, ChefHat, Table2 } from "lucide-react";

export const BusinessHeader = () => (
  <header className="sticky top-0 z-40 bg-brand-light/90 backdrop-blur border-b border-brand-dark/10">
    <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
      <Link to="/business"><BrandLogo /></Link>
      <nav className="hidden md:flex items-center gap-6 text-sm">
        <Link to="/business/features" className="text-brand-dark/70 hover:text-brand-orange">Features</Link>
        <Link to="/business/how-it-works" className="text-brand-dark/70 hover:text-brand-orange">How it works</Link>
        <Link to="/business/pricing" className="text-brand-dark/70 hover:text-brand-orange">Pricing</Link>
        <Link to="/business/contact" className="text-brand-dark/70 hover:text-brand-orange">Contact</Link>
      </nav>
      <Link to="/business/demo" className="bg-brand-orange text-white text-sm font-medium px-4 py-2 rounded-full hover:bg-brand-orange-dark" data-testid="header-book-demo">
        Book a Demo →
      </Link>
    </div>
  </header>
);

export const BusinessFooter = () => (
  <footer className="bg-brand-dark text-brand-light/70 mt-16 py-10">
    <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row justify-between gap-6">
      <div>
        <div className="font-heading font-bold text-xl">
          <span className="text-brand-light">tab</span><span className="text-brand-orange">dine</span>
        </div>
        <div className="text-xs mt-2 max-w-xs">Modern POS + discovery, no app needed. Made for restaurants in India.</div>
      </div>
      <div className="flex gap-8 text-sm">
        <Link to="/business/features" className="hover:text-brand-light">Features</Link>
        <Link to="/business/pricing" className="hover:text-brand-light">Pricing</Link>
        <Link to="/business/how-it-works" className="hover:text-brand-light">How it works</Link>
        <Link to="/business/contact" className="hover:text-brand-light">Contact</Link>
        <Link to="/restaurant/login" className="hover:text-brand-light">Partner Login</Link>
      </div>
    </div>
  </footer>
);

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="p-5 rounded-2xl border border-brand-dark/10 bg-white hover:border-brand-orange/30 transition-colors">
    <div className="w-10 h-10 rounded-lg bg-brand-orange/10 text-brand-orange grid place-items-center">
      <Icon size={18} strokeWidth={1.5} />
    </div>
    <div className="font-heading font-semibold text-brand-dark mt-3">{title}</div>
    <div className="text-sm text-brand-dark/60 mt-1">{desc}</div>
  </div>
);

export default function BusinessHome() {
  return (
    <div className="min-h-screen">
      <BusinessHeader />
      <section className="max-w-6xl mx-auto px-4 pt-10 pb-8 lg:pt-16 lg:pb-12 grid lg:grid-cols-2 gap-8 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-medium text-brand-orange bg-brand-orange/10 rounded-full px-3 py-1 uppercase tracking-widest mb-4">
            <Zap size={13} /> Tabdine
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-dark leading-[1.05] tracking-tight">
            Turn every table<br />into a <span className="text-brand-orange">POS</span>.
          </h1>
          <p className="mt-4 text-brand-dark/70 text-base sm:text-lg max-w-md">
            NFC-first ordering, kitchen display, video menu, loyalty stamps, reviews, offers — all from one browser tab. No app to download.
          </p>
          <div className="mt-6 flex gap-3 flex-wrap">
            <Link to="/business/demo" className="bg-brand-orange text-white px-5 py-3 rounded-full font-medium hover:bg-brand-orange-dark" data-testid="hero-book-demo">
              Book a Free Demo →
            </Link>
            <Link to="/business/pricing" className="border-2 border-brand-dark px-5 py-3 rounded-full text-brand-dark font-medium hover:bg-brand-dark hover:text-brand-light transition-colors">
              See Pricing
            </Link>
          </div>
        </div>
        <div>
          <img src="https://images.pexels.com/photos/19447626/pexels-photo-19447626.jpeg?auto=compress&cs=tinysrgb&w=940" className="rounded-2xl w-full h-80 lg:h-96 object-cover shadow-xl" alt="" />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-10">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-dark">Everything a modern restaurant needs.</h2>
        <div className="grid md:grid-cols-3 gap-3 mt-6">
          <Feature icon={Smartphone} title="NFC + QR ordering" desc="Customers tap or scan at their table. Zero friction, no downloads." />
          <Feature icon={ChefHat} title="Kitchen Display" desc="Tablet-mounted KDS with big fonts, timers, and 1-tap done." />
          <Feature icon={Utensils} title="Video Menu" desc="Short dish clips in a Reels-style feed — customers order what they see." />
          <Feature icon={MessageSquare} title="Reviews tab" desc="Your own reviews page, reply publicly, pin the best ones." />
          <Feature icon={Award} title="Loyalty stamps" desc="Auto-issued digital stamp cards. Reward regulars, effortlessly." />
          <Feature icon={Gift} title="Offers engine" desc="Percent, flat, BOGO, code or auto-apply. Live in one minute." />
          <Feature icon={BarChart3} title="Analytics" desc="Peak hours, top items, revenue trends, tap-tracking." />
          <Feature icon={Table2} title="Per-table QR" desc="Auto-generated QR codes and NFC linking for every seat." />
          <Feature icon={Zap} title="Realtime" desc="Orders flow live from customer → kitchen → served. No refresh." />
        </div>
      </section>

      <section className="bg-brand-dark text-brand-light py-14">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">Ready to see it live?</h2>
          <p className="text-brand-light/70 mt-2">30-minute personalised walkthrough. No commitments.</p>
          <Link to="/business/demo" className="inline-block mt-6 bg-brand-orange px-6 py-3 rounded-full font-medium hover:bg-brand-orange-dark">
            Book a Demo →
          </Link>
        </div>
      </section>

      <BusinessFooter />
    </div>
  );
}
